import { useState, useMemo } from 'react';
import { __ } from '@wordpress/i18n';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger.js';
import { getBlockTypes } from '@wordpress/blocks';
import { categorySection } from './settings';
import * as YouTubeIframeLoader from 'youtube-iframe';
import apiFetch from '@wordpress/api-fetch';
gsap.registerPlugin(ScrollTrigger);

export const richTextSplitUrl = (content) => {
	let url = content.match(
		/^<a.+href="(https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_\+.~#?&\/=]*))".+>.+<\/a>$/
	);
	url = url ? url[1] : null;
	let target = content.match(/^<a.+target="(.+)" .+>.+<\/a>$/);
	target = target ? target[1] : '_self';
	let text = content.match(/^<a.+>(.+)<\/a>$/);
	text = text ? text[1] : null;
	return { url, target, text };
};

export const richTextIsUrl = (value) => {
	return /^<a href="https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_\+.~#?&\/=]*).+>.+<\/a>$/.test(
		value
	);
};

const sanitizeImageObject = (image, format, dimention) => {
	const datas = {
		id: image.id,
		alt: image.alt_text,
		caption: image.caption.rendered,
	};

	let sizes = _.isEmpty(image.media_details.sizes)
		? false
		: image.media_details.sizes;

	// If classic image
	if (sizes) {
		// Calcul of base value (width or height)
		const base =
			typeof format === 'number'
				? format
				: sizes[format]
				  ? sizes[format][dimention]
				  : image.media_details[dimention];

		// Transform sizes object to array
		sizes = Object.values(sizes);

		// Add original size to sizes
		sizes.push({
			width: image.media_details.width,
			height: image.media_details.height,
			source_url: image.source_url,
		});

		// Sort all sizes
		sizes = sizes.sort((a, b) => b.width - a.width);

		// Get query sizes
		const sizesLarger = sizes.filter((size) => size[dimention] >= base);
		const sizeQuery = sizesLarger.length
			? sizesLarger[sizesLarger.length - 1]
			: sizes[0];

		// Get the 2x sizes
		const sizes2xLarger = sizesLarger.filter((size) => {
			return size[dimention] <= base * 2;
		});
		const sizeQuery2x = sizes2xLarger.length ? sizes2xLarger[0] : sizeQuery;

		// Get smaller sizes
		const sizesSmaller = sizes.filter((size) => size[dimention] < base);

		return {
			...datas,
			size: sizeQuery,
			size2x: sizeQuery2x,
			otherSizes: sizes,
			smallerSizes: sizesSmaller,
		};
	}

	// SVG image
	return {
		...datas,
		size: { width: null, height: null, source_url: image.source_url },
		size2x: null,
		otherSizes: [],
		smallerSizes: [],
	};
};

export const getImageHTML = (image, format, dimention = 'width') => {
	dimention =
		['width', 'height', 'full', 'full-big'].indexOf(dimention) + 1
			? dimention
			: 'width';
	const param =
		dimention === 'full' || dimention === 'full-big' ? 'width' : dimention;
	image = sanitizeImageObject(image, format, param);

	const src = `src="${image.size.source_url}"`;
	const alt = image.alt ? `alt="${image.alt}"` : null;

	let srcset = null;
	if (image.otherSizes.length) {
		if (dimention === 'full') {
			srcset = `srcset="${image.otherSizes
				.map((size) => `${size.source_url} ${size.width}w`)
				.join(', ')}"`;
			srcset += ` sizes="(max-width: ${image.otherSizes[0].width}px) 100vw, ${image.otherSizes[0].width}px"`;
		} else if (dimention === 'full-big') {
			srcset = null;
		} else if (image.smallerSizes.length || image.size2x) {
			srcset = '';
			if (image.smallerSizes.length) {
				srcset += image.smallerSizes
					.map((size) => `${size.source_url} ${size.width}w`)
					.join(', ');
			}
			if (image.size2x) {
				srcset += srcset
					? `, ${image.size2x.source_url} 2x`
					: `${image.size2x.source_url} 2x`;
			}

			srcset = `srcset="${srcset}"`;
		}
	}

	let html = `<img ${src}`;
	html += srcset ? ` ${srcset}` : '';
	html += alt ? ` ${alt}` : '';
	html += '/>';
	return html;
};

export const getImageUrl = (image, format, dimention = 'width') => {
	dimention =
		['width', 'height'].indexOf(dimention) + 1 ? dimention : 'width';
	image = sanitizeImageObject(image, format, dimention);
	return image.size.source_url;
};

export const getCSSVarColor = (value) => {
	if (/#[a-fA-F0-9]{6}/.test(value)) {
		return value;
	}
	return `var(--wp--preset--color--${value})`;
};

export const useEmptyAutoAdd = (content) => {
	const [empty, setEmpty] = useState(null);
	if (empty === null) {
		if (!content || content.length === 0) {
			setEmpty(true);
		} else {
			setEmpty(false);
		}
	}

	return [empty, setEmpty];
};

export const gsapAppearTitle = (timeline, title) => {
	if (title) {
		title.innerHTML = title.innerHTML
			.split('<br>')
			.map((line) => `<span><span>${line}</span></span>`)
			.join('');
		const titleLines = title.querySelectorAll('span span');

		timeline.from(titleLines, {
			yPercent: 100,
			stagger: 0.1,
			duration: 0.4,
		});

		return timeline;
	}
	return timeline;
};

export const gsapPlayer = (source, url, target) => {
	target.addEventListener('click', () => {
		// Add the preview DOM
		const preview = document.createElement('div');
		preview.classList.add('preview-video');
		preview.innerHTML = `<div class="preview-inner">
			<div class="preview-background"></div>
			<div class="preview-curve">
				<div class="preview-curve-inner"></div>
			</div>
			<div class="preview-cross"></div>
			<div class="preview-loader">
				<span></span>
				<span></span>
				<span></span>
			</div>
			<div class="preview-content">
				<div class="preview-wrapper">
					<div class="video-wrapper"></div>
				</div>
			</div>
		</div>`;
		document.body.appendChild(preview);

		// Init logic variable
		let init = false;
		let player;

		// Define preview DOM elements
		const body = document.querySelector('body');
		const background = preview.querySelector('.preview-background');
		const curve = preview.querySelector('.preview-curve-inner');
		const loader = preview.querySelector('.preview-loader');
		const cross = preview.querySelector('.preview-cross');
		const wrapper = preview.querySelector('.preview-wrapper');
		const videoWrapper = preview.querySelector('.video-wrapper');

		const getYoutubeId = (url) => {
			const regex =
				/^.*(youtu\.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
			const match = url.match(regex);
			if (match && match[2].length == 11) {
				return match[2];
			}
			return null;
		};

		const hydratePlayerTimeline = (timeline) => {
			timeline
				.add(() => {
					const wrapperRatio =
						wrapper.getBoundingClientRect().width /
						wrapper.getBoundingClientRect().height;

					const wrappeVideoRatio =
						source === 'file'
							? videoWrapper.getBoundingClientRect().width /
							  videoWrapper.getBoundingClientRect().height
							: 16 / 9;

					if (wrapperRatio < wrappeVideoRatio) {
						wrapper.classList.add('landscape');
					} else {
						wrapper.classList.add('portrait');
					}
				})
				.to(loader, {
					opacity: 0,
					duration: 0.2,
				})
				.to(videoWrapper, {
					visibility: 'visible',
					duration: 0,
				})
				.to([videoWrapper, cross], {
					opacity: 1,
					duration: 0.2,
				});
			return timeline;
		};

		const hydrateTimeline = (timeline) => {
			timeline
				.add(() => {
					body.classList.add('is-freeze');
				})
				.to(preview, {
					visibility: 'visible',
					duration: 0,
				})
				.from(background, {
					yPercent: 100,
					duration: 0.6,
					clearProps: 'all',
				})
				.from(
					curve,
					{
						scaleY: 0,
						duration: 0.6,
						clearProps: 'all',
					},
					'-=0.4'
				)
				.to(loader, {
					opacity: 1,
					duration: 0.2,
				})
				.set(loader, { className: 'preview-loader loaded' });

			return timeline;
		};

		const addPlayerStep = (timeline) => {
			timeline.add(
				() => {
					let tlPlayer = gsap.timeline({ paused: true });

					if (source === 'file') {
						videoWrapper.innerHTML = `<video src="${url}"/>`;
						player = videoWrapper.querySelector('video');

						player.addEventListener('loadeddata', (e) => {
							if (player.readyState >= 3) {
								init = true;
								tlPlayer = hydratePlayerTimeline(tlPlayer);
								tlPlayer.add(() => player.play());
								tlPlayer.play();
							}
						});
					} else if (source == 'youtube') {
						videoWrapper.innerHTML = '<div id="player"></div>';
						videoWrapper.classList.add('youtube-wrapper');
						YouTubeIframeLoader.load(function (YT) {
							player = new YT.Player('player', {
								height: '390',
								width: '640',
								videoId: getYoutubeId(url),
								playerVars: {
									playsinline: 1,
									controls: 0,
									color: '#ffc845',
									autohide: 1,
									showinfo: 0,
									wmode: 'opaque',
								},
								events: {
									onReady: onPlayerReady,
								},
							});
						});

						function onPlayerReady(event) {
							init = true;
							tlPlayer.add(() => event.target.playVideo());
							setTimeout(() => {
								tlPlayer = hydratePlayerTimeline(tlPlayer);
								tlPlayer.play();
							}, 500);
						}
					}
				},
				source === 'youtube' ? '' : '+=2'
			);

			return timeline;
		};

		const closePreview = () => {
			if (init) {
				gsap.timeline()
					.to([cross, videoWrapper], {
						opacity: 0,
						duration: 0.2,
					})
					.add(() => {
						if (source === 'file') {
							player.pause();
						} else if (source === 'youtube') {
							player.stopVideo();
						}
						tl.reverse(0.8);
					});

				setTimeout(() => {
					body.classList.remove('is-freeze');
					preview.remove();
				}, 1100);
			} else {
				tl.reverse(0.8);
				setTimeout(() => {
					body.classList.remove('is-freeze');
					preview.remove();
				}, 900);
			}
		};

		// Make timeline
		let tl = gsap.timeline({ paused: true });
		tl = hydrateTimeline(tl);
		tl = addPlayerStep(tl);

		// Launch timeline
		tl.play();

		// Add preview reverse events
		preview.addEventListener('click', (e) => {
			const isVideoWrapper = e.target.closest('.video-wrapper');
			if (!isVideoWrapper) {
				closePreview();
			}
		});
		window.addEventListener('resize', closePreview);
	});
};

export const isValidYoutubeUrl = (value) => {
	const regex =
		/^((?:https?:)?\/\/)?((?:www|m)\.)?((?:youtube\.com|youtu.be))(\/(?:[\w\-]+\?v=|embed\/|v\/)?)([\w\-]+)(\S+)?$/;

	return regex.test(value);
};

export const isValidUrl = (value) => {
	const regex =
		/^https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_\+.~#?&\/=]*)$/;

	return regex.test(value);
};

export const syncPostByLink = (link, blockProps, valueKey, postTypes) => {
	const { setAttributes } = blockProps;
	const [siteUrl, setSiteUrl] = useState('');

	apiFetch({ path: '' }).then((response) => {
		setSiteUrl(response.url);
	});

	useMemo(() => {
		if (siteUrl && link && isValidUrl(link)) {
			const types = `(${postTypes.join('|')})`;
			const regex = new RegExp(
				`^${siteUrl}/${types}/([a-zA-Z0-9-_]+)/?$`
			);
			const matches = link.match(regex);
			const type = matches ? matches[1] : null;
			const slug = matches ? matches[2] : null;

			if (slug) {
				apiFetch({ path: `/wp/v2/${type}/?slug=${slug}` }).then(
					(response) => {
						if (response && response.length) {
							if (
								response[0].permalink == link ||
								response[0].permalink + '/' == link ||
								response[0].permalink == link + '/'
							) {
								setAttributes({
									[valueKey]: response[0],
								});
							} else {
								setAttributes({ [valueKey]: null });
							}
						} else {
							setAttributes({ [valueKey]: null });
						}
					}
				);
			} else {
				setAttributes({ [valueKey]: null });
			}
		} else {
			setAttributes({ [valueKey]: null });
		}
	}, [link, siteUrl]);
};

export const containerBlocksChildren = (name) => {
	let blocks = getBlockTypes();
	blocks = blocks.filter((block) => {
		if (
			block.category !== categorySection &&
			((!block.parent && !block.ancestor) ||
				(block.parent && block?.parent.indexOf(name) + 1))
		) {
			return true;
		}
		return false;
	});
	blocks = blocks.map((block) => block.name);
	return blocks;
};

export const showPopinResa = () => {
	const body = document.querySelector('body');
	const popinResa = document.querySelector('.popin-resa');
	body.classList.add('is-freeze');
	popinResa.classList.add('is-visible');
};

function getScript(source, callback) {
	var script = document.createElement('script');
	var prior = document.getElementsByTagName('script')[0];
	script.async = 1;

	script.onload = script.onreadystatechange = function (_, isAbort) {
		if (
			isAbort ||
			!script.readyState ||
			/loaded|complete/.test(script.readyState)
		) {
			script.onload = script.onreadystatechange = null;
			script = undefined;

			if (!isAbort && callback) setTimeout(callback, 0);
		}
	};

	script.src = source;
	prior.parentNode.insertBefore(script, prior);
}

export const showPopinEvent = (eventId) => {
	if (eventId) {
		fetch(`/wp-json/le-palais/v1/gform/${eventId}`)
			.then((response) => response.json())
			.then((data) => {
				if (data?.formHTML) {
					Object.values(data.styles).forEach((style) => {
						if (style.src && style.handle !== 'gform_settings') {
							let link = document.createElement('link');
							link.setAttribute('rel', 'stylesheet');
							link.setAttribute('href', style.src);
							document.head.appendChild(link);
						}
					});

					Object.values(data.scripts).forEach((script) => {
						if (script.src) {
							getScript(script.src);
						}
					});

					const formWrapper = document.createElement('div');
					formWrapper.classList.add('popin-eventform');

					formWrapper.innerHTML = `
						<div class="popin-inner">
							<div class="popin-close"></div>
							<div class="popin-wrapper">
								<div class="popin-left">
									<h1 class="popin-title">Inscription à<br><strong>l'évènement !</strong></h1>
									<div class="popin-form">${data.formHTML}</div>
								</div>
								<div class="popin-right">${data.eventBlocks.join('')}</div>
							</div>
						</div>`;

					const form = formWrapper.querySelector('.popin-form form');
					form.removeAttribute('action');
					const inputNbPeople = form.querySelector(
						'.gfield--type-le_palais_event_nb_people input'
					);
					if (inputNbPeople) {
						inputNbPeople.max = data.eventCapacity;
					}
					document.body.appendChild(formWrapper);
					document.body.classList.add('is-freeze');
					formWrapper.classList.add('is-visible');
					formWrapper.addEventListener('click', (e) => {
						const target = e.target;
						const isWrapper = target.closest('.popin-wrapper');
						if (!isWrapper) {
							formWrapper.classList.remove('is-visible');
							document.body.classList.remove('is-freeze');
							setTimeout(() => {
								formWrapper.remove();
							}, 200);
						}
					});
				}
			})
			.catch((error) => {
				console.error(
					'Erreur lors de la récupération du formulaire :',
					error
				);
			});
	}
};
